import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { useStore } from '../context/StoreContext';
import { useToast } from '../hooks/useToast';
import { useScrollReveal } from '../hooks/useParallax';
import ToastContainer from '../components/Toast';
import api from '../utils/api';
import {
  buildCategoriesFromProducts,
  getProductTimestamp,
  normalizeCategory,
  normalizeProduct,
  slugifyCategoryName,
} from '../utils/productUtils';

const PER_PAGE = 8;
const DEFAULT_MAX_PRICE = 15000;

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const [page, setPage]           = useState(1);
  const [sort, setSort]           = useState('default');
  const [colors, setColors]       = useState([]);
  const [materials, setMaterials] = useState([]);
  const [maxPrice, setMaxPrice]   = useState(DEFAULT_MAX_PRICE);
  const [priceFilterActive, setPriceFilterActive] = useState(false);
  const [filterOpen, setFilterOpen] = useState({ price: true, color: true, material: true });
  const [mobileFilter, setMobileFilter] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addToCart } = useStore();
  const { toasts, showToast } = useToast();
  useScrollReveal();

  const catSlug = params.get('category') || 'all';
  const subFilter = params.get('sub') || '';

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const productResponse = await api.get('/products', {
          headers: { 'Cache-Control': 'no-cache' },
        });

        const loaded = (productResponse.data.products || []).map(normalizeProduct);
        setProducts(loaded);

        try {
          const categoryResponse = await api.get('/products/categories', {
            headers: { 'Cache-Control': 'no-cache' },
          });
          const loadedCategories = (categoryResponse.data.categories || []).map(normalizeCategory);
          setCategories(loadedCategories.length ? loadedCategories : buildCategoriesFromProducts(loaded));
        } catch (categoryError) {
          console.error(categoryError);
          setCategories(buildCategoriesFromProducts(loaded));
        }
      } catch (fetchError) {
        console.error(fetchError);
        setError(fetchError.response?.data?.message || fetchError.message || 'Failed to fetch products');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  const ALL_COLORS = useMemo(
    () => [...new Set(products.flatMap(p => p.colors || []).filter(c => c && c !== 'N/A'))],
    [products]
  );

  const ALL_MATERIALS = useMemo(
    () => [...new Set(products.flatMap(p => p.materials || []).filter(m => m && m !== 'N/A'))],
    [products]
  );

  useEffect(() => { setPage(1); }, [catSlug, subFilter, colors, materials, sort, maxPrice, priceFilterActive]);

  const selectedCategory = useMemo(
    () => categories.find((category) => category.slug === catSlug),
    [categories, catSlug]
  );

  const categoryTabs = useMemo(
    () => [
      { slug: 'all', name: 'All Products', icon: 'fa-store' },
      ...categories.map((category) => ({
        slug: category.slug,
        name: category.name,
        icon: category.icon || DEFAULT_CATEGORY_ICON,
      })),
    ],
    [categories]
  );

  const filtered = useMemo(() => {
    let list = [...products];
    if (catSlug !== 'all') {
      if (selectedCategory) {
        list = list.filter(p => p.category?.toLowerCase() === selectedCategory.name.toLowerCase());
      } else {
        list = list.filter(p => slugifyCategoryName(p.category) === catSlug);
      }
    }
    if (subFilter) list = list.filter(p => p.subcategory === subFilter);
    if (colors.length) list = list.filter(p => p.colors.some(c => colors.includes(c)));
    if (materials.length) list = list.filter(p => p.materials.some(m => materials.includes(m)));
    if (priceFilterActive && maxPrice < DEFAULT_MAX_PRICE) {
      list = list.filter(p => p.price <= maxPrice);
    }
    switch (sort) {
      case 'price-asc':  list.sort((a, b) => a.price - b.price); break;
      case 'price-desc': list.sort((a, b) => b.price - a.price); break;
      case 'rating':     list.sort((a, b) => b.rating - a.rating); break;
      case 'newest':     list.sort((a, b) => getProductTimestamp(b) - getProductTimestamp(a)); break;
    }
    return list;
  }, [products, catSlug, selectedCategory, subFilter, colors, materials, maxPrice, priceFilterActive, sort]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const toggleColor    = useCallback(c => setColors(p => p.includes(c) ? p.filter(x => x !== c) : [...p, c]), []);
  const toggleMaterial = useCallback(m => setMaterials(p => p.includes(m) ? p.filter(x => x !== m) : [...p, m]), []);
  const clearAll       = () => { setColors([]); setMaterials([]); setMaxPrice(DEFAULT_MAX_PRICE); setPriceFilterActive(false); setPage(1); };
  const setCategory    = slug => { setParams(slug === 'all' ? {} : { category: slug }); };
  const toggleFilter   = key => setFilterOpen(p => ({ ...p, [key]: !p[key] }));

  const handleAddToCart = p => {
    addToCart({ ...p, variant: `${p.colors[0]} / ${p.sizes[0]}`, qty: 1 });
    showToast(`${p.name} added to cart`, 'fa-bag-shopping');
  };

  const activeFilterCount = colors.length + materials.length + (priceFilterActive && maxPrice < DEFAULT_MAX_PRICE ? 1 : 0);

  const FilterPanel = () => (
    <div className="filter-panel">
      <div className="filter-header">
        <h3 className="filter-title">
          <i className="fas fa-sliders" />Filters
          {activeFilterCount > 0 && (
            <span className="filter-badge">{activeFilterCount}</span>
          )}
        </h3>
        {activeFilterCount > 0 && (
          <button onClick={clearAll} className="filter-clear-btn">Clear all</button>
        )}
      </div>

      {/* Price */}
      <div className="filter-group">
        <div className="filter-group-title" onClick={() => toggleFilter('price')}>
          Price Range <i className={`fas fa-chevron-${filterOpen.price ? 'up' : 'down'}`} />
        </div>
        {filterOpen.price && (
          <>
            <div className="price-range-info">
              <span>₹0</span><span>₹{maxPrice.toLocaleString('en-IN')}</span>
            </div>
            <input type="range" min={0} max={DEFAULT_MAX_PRICE} step={100} value={maxPrice}
              onChange={e => {
                const value = Number(e.target.value);
                setMaxPrice(value);
                setPriceFilterActive(value < DEFAULT_MAX_PRICE);
              }}
              className="price-slider" />
          </>
        )}
      </div>

      {/* Color */}
      {/* <div className="filter-group">
        <div className="filter-group-title" onClick={() => toggleFilter('color')}>
          Color <i className={`fas fa-chevron-${filterOpen.color ? 'up' : 'down'}`} />
        </div>
        {filterOpen.color && (
          <div className="color-chips-container">
            {ALL_COLORS.map(c => (
              <button key={c} className={`filter-chip ${colors.includes(c) ? 'active' : ''}`} onClick={() => toggleColor(c)}>{c}</button>
            ))}
          </div>
        )}
      </div> */}

      {/* Material */}
      {/* <div className="filter-group">
        <div className="filter-group-title" onClick={() => toggleFilter('material')}>
          Material <i className={`fas fa-chevron-${filterOpen.material ? 'up' : 'down'}`} />
        </div>
        {filterOpen.material && (
          <div>
            {ALL_MATERIALS.map(m => (
              <label key={m} className="filter-check">
                <input type="checkbox" checked={materials.includes(m)} onChange={() => toggleMaterial(m)} />
                {m}
              </label>
            ))}
          </div>
        )}
      </div> */}
    </div>
  );

  return (
    <div className="shop-page-container">
      {/* Page header */}
      <div className="shop-header">
        <div className="container">
          <p className="shop-header-subtitle">
            Pandian Store
          </p>
          <h1 className="shop-header-title">
            Shop <em>All Products</em>
          </h1>
          <p className="shop-header-count">
            {filtered.length} products {catSlug !== 'all' && `in ${selectedCategory?.name || ''}`}
          </p>
        </div>
      </div>

      {/* Category tabs */}
      <div className="category-tabs-bar">
        <div className="container category-tabs-wrapper">
          <div className="category-tabs-list">
            {categoryTabs.map(cat => (
              <button 
                key={cat.slug} 
                onClick={() => setCategory(cat.slug)}
                className={`category-tab-btn ${catSlug === cat.slug ? 'active' : 'inactive'}`}
              >
                <i className={`fas ${cat.icon}`} />{cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container shop-content">
        <div className="row g-4">
          {/* Filter sidebar desktop */}
          <div className="col-lg-3 d-none d-lg-block">
            <FilterPanel />
          </div>

          {/* Products */}
          <div className="col-lg-9">
            {/* Sort + filter bar */}
            <div className="controls-bar">
              <p className="results-count">
                Showing <strong>{Math.min((page - 1) * PER_PAGE + 1, filtered.length)}–{Math.min(page * PER_PAGE, filtered.length)}</strong> of <strong>{filtered.length}</strong>
              </p>
              <div className="controls-actions">
                {/* Mobile filter button */}
                <button className="d-lg-none btn-outline-navy mobile-filter-trigger" onClick={() => setMobileFilter(true)}>
                  <i className="fas fa-sliders" />Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                </button>
                <select value={sort} onChange={e => setSort(e.target.value)} className="sort-select">
                  <option value="default">Sort: Default</option>
                  <option value="price-asc">Price: Low → High</option>
                  <option value="price-desc">Price: High → Low</option>
                  <option value="rating">Top Rated</option>
                  <option value="newest">Newest First</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="shop-loading">
                <p>Loading products...</p>
              </div>
            ) : error ? (
              <div className="shop-error">
                <h3>Unable to load products</h3>
                <p>{error}</p>
                <button className="btn-outline-navy" onClick={() => window.location.reload()}>Retry</button>
              </div>
            ) : (
              <>
                {/* Active filter chips */}
                {activeFilterCount > 0 && (
                  <div className="active-chips-container">
                    {colors.map(c => (
                      <span key={c} className="active-filter-chip">
                        {c} <button onClick={() => toggleColor(c)}>✕</button>
                      </span>
                    ))}
                    {materials.map(m => (
                      <span key={m} className="active-filter-chip">
                        {m} <button onClick={() => toggleMaterial(m)}>✕</button>
                      </span>
                    ))}
                  </div>
                )}

                {paginated.length === 0 ? (
                  <div className="no-results">
                    <i className="fas fa-search" />
                    <h3>No products found</h3>
                    <p>Try adjusting your filters</p>
                    <button className="btn-outline-navy" onClick={clearAll}>Clear All Filters</button>
                  </div>
                ) : (
                  <div className="row g-4">
                    {paginated.map(p => (
                      <div key={p.id} className="col-6 col-md-4 col-xl-4">
                        <ProductCard product={p} onAddToCart={handleAddToCart} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button className="page-btn" onClick={() => setPage(1)} disabled={page === 1} aria-label="First page">
                  <i className="fas fa-angles-left" />
                </button>
                <button className="page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} aria-label="Previous page">
                  <i className="fas fa-angle-left" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                  .reduce((acc, n, i, arr) => { if (i > 0 && n - arr[i - 1] > 1) acc.push('…'); acc.push(n); return acc; }, [])
                  .map((n, i) => n === '…'
                    ? <span key={`e${i}`} className="pagination-ellipsis">…</span>
                    : <button key={n} className={`page-btn ${page === n ? 'active' : ''}`} onClick={() => setPage(n)}>{n}</button>
                  )}
                <button className="page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} aria-label="Next page">
                  <i className="fas fa-angle-right" />
                </button>
                <button className="page-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages} aria-label="Last page">
                  <i className="fas fa-angles-right" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {mobileFilter && (
        <div className="mobile-drawer-overlay">
          <div className="mobile-drawer-backdrop" onClick={() => setMobileFilter(false)} />
          <div className="mobile-drawer-content">
            <div className="mobile-drawer-header">
              <h3>Filters</h3>
              <button onClick={() => setMobileFilter(false)} className="mobile-drawer-close">✕</button>
            </div>
            <FilterPanel />
            <button className="btn-gold mobile-drawer-apply-btn" onClick={() => setMobileFilter(false)}>
              Apply Filters
            </button>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} />
    </div>
  );
}